import React, { Component } from "react";
import PropTypes from "prop-types";
import { msInSeconds } from "lib/temporal/time";

/**
 * Audio component for timeline playback
 * This component handles HTML5 Audio playback synchronized with timeline
 */
class Audio extends Component {
  constructor(props) {
    super(props);

    this.audioRef = React.createRef();
    this.playTimeout = null;
    this.state = {
      isLoaded: false,
      error: null
    };
  }

  componentDidMount() {
    this.initializeAudio();
  }

  componentDidUpdate(prevProps) {
    const { isPlaying, isPaused, elementData, currentPageId, pageId } =
      this.props;

    // Stop audio if this page is no longer the current page
    if (currentPageId !== pageId && prevProps.currentPageId === pageId) {
      this.handlePause();
      // Clear any pending play timeouts
      if (this.playTimeout) {
        clearTimeout(this.playTimeout);
        this.playTimeout = null;
      }
      return;
    }

    // Resume audio if this page just became the current page and should be playing
    if (
      currentPageId === pageId &&
      prevProps.currentPageId !== pageId &&
      isPlaying
    ) {
      this.handlePlay();
      return;
    }

    // Handle play/pause changes
    if (isPlaying !== prevProps.isPlaying) {
      if (isPlaying) {
        // Check if we're resuming from pause
        const isResumingFromPause = prevProps.isPaused && !isPaused;
        this.handlePlay(isResumingFromPause);
      } else {
        this.handlePause();
      }
    }

    // Handle element data changes (delay, trim, volume, mute)
    if (prevProps.elementData !== elementData && this.audioRef.current) {
      this.updateAudioProperties();
    }

    // Handle src changes
    if (prevProps.elementData?.src !== elementData?.src) {
      this.initializeAudio();
    }
  }

  componentWillUnmount() {
    // Clear any pending play timeout
    if (this.playTimeout) {
      clearTimeout(this.playTimeout);
      this.playTimeout = null;
    }

    if (this.audioRef.current) {
      this.audioRef.current.pause();
      this.audioRef.current.src = "";
    }
  }

  initializeAudio = () => {
    const { elementData } = this.props;

    if (!elementData?.src || !this.audioRef.current) return;

    const audio = this.audioRef.current;

    // Set up audio element
    audio.src = elementData.src;
    audio.preload = "auto";
    audio.crossOrigin = "anonymous";

    // Set initial properties
    this.updateAudioProperties();

    // Handle load events
    audio.addEventListener("loadeddata", this.handleAudioLoaded);
    audio.addEventListener("error", this.handleAudioError);
  };

  updateAudioProperties = () => {
    const { elementData } = this.props;

    if (!elementData || !this.audioRef.current) return;

    const audio = this.audioRef.current;

    // Update volume and mute state
    audio.volume = elementData.volume || 1.0;
    audio.muted = elementData.isMuted || false;
  };

  handleAudioLoaded = () => {
    this.setState({ isLoaded: true, error: null });
  };

  handleAudioError = error => {
    console.warn("Audio loading error:", error);
    this.setState({
      error: error.message || "Failed to load audio",
      isLoaded: false
    });
  };

  handlePlay = (isResumingFromPause = false) => {
    const { elementData, currentPlayingTime } = this.props;

    if (!this.audioRef.current || !elementData || !this.state.isLoaded) return;

    const audio = this.audioRef.current;

    const delay = elementData.delay || 0;
    const trimStart = elementData.trimStart || 0;
    const trimEnd = elementData.trimEnd || 0;
    const maxPlayTime = msInSeconds(elementData.duration - trimEnd);

    let audioStartTime;
    let playbackDelay;

    if (isResumingFromPause && currentPlayingTime !== undefined) {
      // Resuming from pause: use currentPlayingTime to calculate audio position
      // Audio position = trimStart + (currentPlayingTime - delay)
      // This accounts for where we are in the timeline relative to when audio should start
      const timelinePosition = currentPlayingTime;
      const timeSinceAudioStart = timelinePosition - delay;

      if (timeSinceAudioStart < 0) {
        // Timeline is paused before audio delay - audio hasn't started yet
        // When we resume, we need to wait for the remaining delay
        audioStartTime = trimStart;
        playbackDelay = Math.abs(timeSinceAudioStart);
      } else {
        // Timeline is paused after audio has started playing
        // Resume from the correct position in the audio file
        audioStartTime = trimStart + timeSinceAudioStart;
        playbackDelay = 0;

        // Safety check: don't seek beyond the audio duration
        if (audioStartTime >= elementData.duration - trimEnd) {
          // Audio has already ended
          return;
        }
      }
    } else {
      // Starting fresh from the beginning
      audioStartTime = trimStart;
      playbackDelay = delay + trimStart;

      // If audio starts before timeline (negative delay), we need to skip ahead in the audio
      if (playbackDelay < 0) {
        // Audio has been playing for Math.abs(playbackDelay) milliseconds when timeline starts
        audioStartTime = trimStart + Math.abs(playbackDelay);
        playbackDelay = 0; // Start immediately when timeline plays

        // Safety check: don't start beyond the audio duration
        if (audioStartTime >= elementData.duration - trimEnd) {
          // Audio would have already ended by the time timeline starts
          return;
        }
      }
    }

    try {
      // Start from calculated position in the audio file
      audio.currentTime = msInSeconds(audioStartTime);

      // Set up event listener to stop at trimEnd
      const handleTimeUpdate = () => {
        if (audio.currentTime >= maxPlayTime) {
          audio.pause();
          audio.removeEventListener("timeupdate", handleTimeUpdate);
        }
      };

      // Remove any existing listener and add new one
      audio.removeEventListener("timeupdate", this.handleTimeUpdate);
      this.handleTimeUpdate = handleTimeUpdate;
      audio.addEventListener("timeupdate", handleTimeUpdate);

      // Use the calculated playback delay
      if (playbackDelay > 0) {
        // Clear any existing timeout
        if (this.playTimeout) {
          clearTimeout(this.playTimeout);
        }
        // Store the timeout so it can be cleared if needed
        this.playTimeout = setTimeout(() => {
          if (this.audioRef.current && this.props.isPlaying) {
            const playPromise = audio.play();
            if (playPromise !== undefined) {
              playPromise.catch(error => {
                console.warn("Audio play failed:", error);
              });
            }
          }
          this.playTimeout = null;
        }, playbackDelay);
      } else {
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.warn("Audio play failed:", error);
          });
        }
      }
    } catch (error) {
      console.warn("Audio play error:", error);
    }
  };

  handlePause = () => {
    if (!this.audioRef.current) return;

    try {
      // Clear any pending play timeout
      if (this.playTimeout) {
        clearTimeout(this.playTimeout);
        this.playTimeout = null;
      }

      const audio = this.audioRef.current;
      audio.pause();

      // Clean up timeupdate listener
      if (this.handleTimeUpdate) {
        audio.removeEventListener("timeupdate", this.handleTimeUpdate);
        this.handleTimeUpdate = null;
      }
    } catch (error) {
      console.warn("Audio pause error:", error);
    }
  };

  render() {
    // Audio elements are invisible in the editor
    return (
      <audio
        ref={this.audioRef}
        style={{ display: "none" }}
        onLoadedData={this.handleAudioLoaded}
        onError={this.handleAudioError}
      />
    );
  }
}

Audio.propTypes = {
  elementData: PropTypes.object.isRequired,
  isPlaying: PropTypes.bool,
  isPaused: PropTypes.bool,
  currentPlayingTime: PropTypes.number,
  pageId: PropTypes.string,
  currentPageId: PropTypes.string
};

Audio.defaultProps = {
  isPlaying: false,
  isPaused: false,
  currentPlayingTime: 0,
  pageId: null,
  currentPageId: null
};

export default Audio;
